import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { WorkflowGraphApiData } from './workflow-graph.types';

/** What `client.resource.get` resolves to, so callers need not restate it. */
export type ResourceGetResult = Awaited<
  ReturnType<TenantedClient['resource']['get']>
>;

/** The fields of a fetched workflow that the nested-workflow views read. */
export type WorkflowResourceSnapshot = {
  readonly id: string;
  readonly name: string;
  readonly graph: WorkflowGraphApiData;
  /**
   * Carried so a whole-graph `update` can echo it back. Omitting it on an update
   * clears the workflow's dedupe config, which is not something reading a graph
   * should ever do.
   */
  readonly idempotencyKey:
    | { strategy: 'value'; pointer: string }
    | { strategy: 'value'; template: string }
    | null;
};

/**
 * Narrows a `resource.get` response to a workflow, or null if it is not one.
 *
 * Every caller was doing this by hand — `isOk()`, then the `type === 'workflow'`
 * discriminant, then reaching into `.workflow` — and each also had to assert the
 * graph's shape, because the API types it as `unknown`. Keeping that in one place
 * means one cast with one explanation instead of three that can drift.
 *
 * The cast is unavoidable at this boundary: `graph` is `unknown` on the wire and
 * `WorkflowGraphApiData` is the shape the whole workflow UI already reads it as
 * (see `workflow.model.ts`, which does the same).
 *
 * A response with no `graph` reads as null rather than as an empty workflow. One
 * of this helper's callers feeds a whole-graph `update`, so substituting empty
 * collections would turn a missing field into deleting every node the workflow
 * has. Unreadable has to stay distinguishable from empty.
 */
export function readWorkflowResource(
  result: ResourceGetResult | undefined
): WorkflowResourceSnapshot | null {
  if (
    result === undefined ||
    !result.isOk() ||
    result.value.type !== 'workflow'
  ) {
    return null;
  }
  const workflow = result.value.workflow;
  const graph = workflow.graph as WorkflowGraphApiData | undefined;
  if (graph === undefined) {
    return null;
  }
  return {
    id: workflow.id,
    name: workflow.name,
    graph,
    idempotencyKey: workflow.idempotencyKey ?? null,
  };
}
